import { bilingual, type Lang } from "./i18n";

const IDS = ["intro", "tutorial", "tour", "offer", "feature", "proof", "series"] as const;
export type CreationIntent = (typeof IDS)[number];
export const IDEA_IDS = [...IDS] as [CreationIntent, ...CreationIntent[]];

type IdeaText = Record<CreationIntent, { label: string; prompt: string }>;
/** Each starting point's name and what it asks the Director for. */
export const IDEA_TEXT = bilingual<IdeaText>({
  en: {
    intro: {
      label: "Launch",
      prompt: "Introduce the product in a promotional video.",
    },
    tutorial: {
      label: "Tutorial",
      prompt:
        "Make a tutorial video that teaches, step by step, how to use the product for one key task: open on the result, then numbered steps shown on the product's real screens or photos.",
    },
    tour: {
      label: "Site tour",
      prompt:
        "Make a promotional video that tours the product's website or app and shows how it works.",
    },
    offer: {
      label: "Offer",
      prompt:
        "Make a promotional video that sells a specific offer, deal or price the product has.",
    },
    feature: {
      label: "Feature",
      prompt: "Focus this video on a standout product feature.",
    },
    proof: {
      label: "Social proof",
      prompt:
        "Make a promotional video built on the product's real social proof: reviews, ratings, results or testimonials.",
    },
    series: {
      label: "Series",
      prompt: "Create a series of promotional videos for the product.",
    },
  },
  es: {
    intro: {
      label: "Lanzamiento",
      prompt: "Presenta el producto en un vídeo promocional.",
    },
    tutorial: {
      label: "Tutorial",
      prompt:
        "Haz un vídeo tutorial que enseñe, paso a paso, cómo usar el producto para una tarea clave: empieza por el resultado y sigue con los pasos numerados sobre sus pantallas o fotos reales.",
    },
    tour: {
      label: "Recorrido web",
      prompt:
        "Haz un vídeo promocional que recorra la web o la app del producto y muestre cómo funciona.",
    },
    offer: {
      label: "Oferta",
      prompt:
        "Haz un vídeo promocional que venda una oferta, promoción o precio concreto del producto.",
    },
    feature: {
      label: "Función destacada",
      prompt: "Centra este vídeo en una función destacada del producto.",
    },
    proof: {
      label: "Reseñas",
      prompt:
        "Haz un vídeo promocional basado en la prueba social real del producto: reseñas, valoraciones, resultados o testimonios.",
    },
    series: {
      label: "Serie",
      prompt: "Crea una serie de vídeos promocionales para el producto.",
    },
  },
});

export interface Idea {
  id: CreationIntent;
  label: string;
  prompt: string;
}
/** The starting points, in order, in `lang`. */
export const ideas = (lang: Lang = "en"): Idea[] =>
  IDEA_IDS.map((id) => ({ id, ...IDEA_TEXT[lang][id] }));
export const ideaFor = (id?: string | null, lang: Lang = "en") =>
  ideas(lang).find((idea) => idea.id === id);

const TITLE = bilingual({
  en: { video: "Video", fresh: "New video" },
  es: { video: "Vídeo", fresh: "Nuevo vídeo" },
});

/** A free, immediate working title. No extra inference or hidden credit spend. */
export function creationTitle(
  text: string,
  intent?: CreationIntent | null,
  brand = "",
  lang: Lang = "en",
) {
  const idea = ideaFor(intent, lang);
  const link = text.match(/https?:\/\/[^\s<>]+/i)?.[0];
  let product = brand.trim();
  if (!product && link) {
    try {
      const host = new URL(link).hostname.replace(/^www\./, "");
      product = host.split(".")[0].replace(/[-_]+/g, " ");
      product = product.charAt(0).toUpperCase() + product.slice(1);
    } catch {
      /* The conversation can clarify an incomplete URL. */
    }
  }
  if (product)
    return `${product} · ${idea?.label ?? TITLE[lang].video}`.slice(0, 100);
  const words = text
    .trim()
    .split(/\n/)[0]
    .replace(
      /^(?:please\s+)?(?:make|create|produce|build)\s+(?:(?:me|us)\s+)?(?:a\s+)?(?:(?:short|launch|promotional|product)\s+)?video\s+(?:for|about|of)\s+/i,
      "",
    )
    .replace(/^(?:please\s+)?promote\s+/i, "")
    // The same requests in Spanish: "haz un vídeo para …", "crea un vídeo de …".
    .replace(
      /^[¡¿]?(?:por\s+favor,?\s+)?(?:(?:haz|cr[eé]a|prep[aá]ra|gen[eé]ra|prod[uú]ce)(?:me|nos)?|hagamos|quiero|necesito)\s+(?:un\s+)?(?:(?:breve|corto|peque[ñn]o)\s+)?v[ií]deo\s+(?:(?:corto|promocional|publicitario|de\s+lanzamiento|de\s+producto)\s+)?(?:para|sobre|de|del)\s+/i,
      "",
    )
    .replace(/^[¡¿]?(?:por\s+favor,?\s+)?promociona(?:r)?\s+/i, "")
    .replace(/[.!?]+$/, "")
    .replace(/\s+/g, " ");
  if (!words) return idea?.label ?? TITLE[lang].fresh;
  const shortened =
    words.length > 65 ? words.slice(0, 62).replace(/\s+\S*$/, "") + "…" : words;
  return shortened.charAt(0).toUpperCase() + shortened.slice(1);
}
